import {
  type FC,
  type ReactNode,
  memo,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useDropdownPosition } from './useDropdownPosition';
import { useSelectKeyboardNavigation } from './useSelectKeyboardNavigation';
import { KawaiiSelectButton } from './KawaiiSelectButton';
import { KawaiiSelectDropdown } from './KawaiiSelectDropdown';
import { PortalManager } from './PortalManager';

interface Option {
  value: string | number;
  label: ReactNode;
  renderOption?: (option: Option) => ReactNode;
  renderButton?: (option: Option) => ReactNode;
}

interface KawaiiSelectProps {
  value?: string | number;
  onChange: (value: string | number) => void;
  options: Option[];
  placeholder?: ReactNode;
  className?: string;
  optionClassName?: string;
}

const KawaiiSelectComponent: FC<KawaiiSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option...',
  className,
  optionClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === value);
  }, [options, value]);

  // --- Use Custom Hooks ---
  const { dropdownStyle, opensUpward } = useDropdownPosition(
    buttonRef,
    isOpen,
    options.length,
    300,
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const {
    focusedIndex,
    getOptionRef,
    handleDropdownKeyDown,
    handleButtonKeyDown: handleNavButtonKeyDown, // Rename to avoid clash
  } = useSelectKeyboardNavigation({
    options,
    isOpen,
    onChange,
    closeDropdown,
    buttonRef,
    dropdownRef, // Pass dropdown ref to the hook
    opensUpward,
    value,
  });

  // --- Event Handlers ---
  const handleTriggerClick = () => {
    setIsOpen((prevOpen) => !prevOpen); // Toggle open state
  };

  const handleOptionClick = useCallback(
    (selectedValue: string | number) => {
      onChange(selectedValue);
      closeDropdown();
      buttonRef.current?.focus();
    },
    [onChange, closeDropdown],
  );

  const handleButtonKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      handleNavButtonKeyDown(event);
      if (event.key === 'Enter' || event.key === ' ') {
        // Let onClick handle the toggle primarily
      }
      if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        setIsOpen(true);
      }
    },
    [handleNavButtonKeyDown, isOpen],
  );

  // --- Click Outside Logic ---
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the button AND the dropdown
      if (
        !buttonRef.current?.contains(event.target as Node) &&
        !dropdownRef.current?.contains(event.target as Node) // Check dropdown ref too!
      ) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Scroll/resize listeners are now handled by useDropdownPosition hook!
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, closeDropdown]); // Add closeDropdown dependency

  return (
    <div className="w-full">
      {' '}
      {/* Removed relative positioning, button handles its glow */}
      <KawaiiSelectButton
        ref={buttonRef}
        value={value}
        isOpen={isOpen}
        placeholder={placeholder}
        className={className}
        selectedOptionLabel={
          selectedOption?.renderButton
            ? selectedOption.renderButton(selectedOption)
            : selectedOption?.label
        }
        onClick={handleTriggerClick}
        onKeyDown={handleButtonKeyDown}
      />
      <PortalManager isOpen={isOpen}>
        <KawaiiSelectDropdown
          dropdownRef={dropdownRef} // Pass the ref here
          options={options}
          value={value}
          optionClassName={optionClassName}
          dropdownStyle={dropdownStyle}
          opensUpward={opensUpward}
          focusedIndex={focusedIndex}
          handleDropdownKeyDown={handleDropdownKeyDown}
          getOptionRef={getOptionRef}
          handleOptionClick={handleOptionClick}
        />
      </PortalManager>
    </div>
  );
};

// Wrap with memo for performance magic! ✨
export const KawaiiSelect = memo(KawaiiSelectComponent);
